import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'

const Header: React.FC = () => {
  const [showUserMenu, setShowUserMenu] = useState(false)
  const navigate = useNavigate()

  // Get user info from localStorage
  const userStr = localStorage.getItem('user')
  const user = userStr ? JSON.parse(userStr) : null

  // Get branding settings from localStorage
  const [branding, setBranding] = useState({
    systemName: 'PIS',
    systemFullName: 'Pathology Information System',
    systemIcon: '🏥',
    logoUrl: ''
  })

  useEffect(() => {
    const loadBranding = async () => {
      try {
        // Try to fetch from API first
        const response = await fetch('/api/v1/settings/branding/active/')

        if (response.ok) {
          const data = await response.json()
          setBranding({
            systemName: data.systemName || 'PIS',
            systemFullName: data.systemFullName || 'Pathology Information System',
            systemIcon: data.systemIcon || '🏥',
            logoUrl: data.logoUrl || ''
          })
          // Cache in localStorage
          localStorage.setItem('brandingSettings', JSON.stringify(data))
        } else {
          // Fallback to localStorage
          const savedSettings = localStorage.getItem('brandingSettings')
          if (savedSettings) {
            const parsed = JSON.parse(savedSettings)
            setBranding({
              systemName: parsed.systemName || 'PIS',
              systemFullName: parsed.systemFullName || 'Pathology Information System',
              systemIcon: parsed.systemIcon || '🏥',
              logoUrl: parsed.logoUrl || ''
            })
          }
        }
      } catch (error) {
        console.error('Failed to load branding settings from API:', error)
        // Fallback to localStorage
        const savedSettings = localStorage.getItem('brandingSettings')
        if (savedSettings) {
          try {
            const parsed = JSON.parse(savedSettings)
            setBranding({
              systemName: parsed.systemName || 'PIS',
              systemFullName: parsed.systemFullName || 'Pathology Information System',
              systemIcon: parsed.systemIcon || '🏥',
              logoUrl: parsed.logoUrl || ''
            })
          } catch (parseError) {
            console.error('Failed to parse localStorage settings:', parseError)
          }
        }
      }
    }

    loadBranding()

    // Listen for branding updates (from other tabs)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'brandingSettings') {
        loadBranding()
      }
    }

    // Listen for branding updates (from same tab)
    const handleBrandingUpdate = () => {
      loadBranding()
    }

    window.addEventListener('storage', handleStorageChange)
    window.addEventListener('brandingUpdated', handleBrandingUpdate)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('brandingUpdated', handleBrandingUpdate)
    }
  }, [])

  const handleLogout = () => {
    localStorage.removeItem('authToken')
    localStorage.removeItem('user')
    toast.success('Logged out successfully')
    navigate('/login')
  }

  const currentTime = new Date().toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit'
  })

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-40">
      <div className="px-6">
        <div className="flex justify-between items-center h-16">
          {/* Left side - System title and breadcrumb */}
          <div className="flex items-center space-x-4">
            <div className="flex items-center">
              <h1 className="text-xl font-bold text-gray-900 flex items-center space-x-2">
                {branding.logoUrl ? (
                  <img
                    src={branding.logoUrl}
                    alt="Logo"
                    className="h-8 w-8 object-contain"
                  />
                ) : (
                  <span className="text-2xl">{branding.systemIcon}</span>
                )}
                <span>{branding.systemName} - {branding.systemFullName}</span>
              </h1>
            </div>
            <div className="hidden md:block">
              <nav className="flex items-center space-x-2 text-sm text-gray-500">
                <span>Control Panel</span>
                <span>/</span>
                <span className="text-gray-900 font-medium">Dashboard</span>
              </nav>
            </div>
          </div>

          {/* Right side - User info and controls */}
          <div className="flex items-center space-x-4">
            {/* System indicators */}
            <div className="hidden lg:flex items-center space-x-4 text-sm text-gray-600">
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                <span>System Online</span>
              </div>
              <div className="flex items-center space-x-1">
                <span>🕐</span>
                <span>{currentTime}</span>
              </div>
            </div>

            {/* Notifications */}
            <button className="relative p-2 text-gray-400 hover:text-gray-600 transition-colors">
              <span className="text-lg">🔔</span>
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
            </button>

            {/* Quick search */}
            <div className="hidden md:block">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Quick search..."
                  className="w-64 pl-8 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <span className="absolute left-2.5 top-2.5 text-gray-400">🔍</span>
              </div>
            </div>

            {/* User menu */}
            {user && (
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="text-xl">{user.avatar}</div>
                  <div className="hidden md:block text-left">
                    <p className="text-sm font-medium text-gray-900">{user.name}</p>
                    <p className="text-xs text-gray-500">
                      {user.organization_name || user.role || 'User'}
                    </p>
                  </div>
                  <svg
                    className={`w-4 h-4 text-gray-400 transition-transform ${showUserMenu ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* User dropdown menu */}
                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-50">
                    <div className="py-1">
                      <div className="px-4 py-3 border-b border-gray-100">
                        <p className="text-sm font-medium text-gray-900">{user.name}</p>
                        <p className="text-xs text-gray-500">{user.username}</p>
                        {user.organization_name && (
                          <p className="text-xs text-blue-600 mt-1">🏥 {user.organization_name}</p>
                        )}
                      </div>
                      <a
                        href="#"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <span className="mr-3">👤</span>
                        Profile Settings
                      </a>
                      <a
                        href="#"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <span className="mr-3">🔧</span>
                        Preferences
                      </a>
                      <a
                        href="#"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <span className="mr-3">❓</span>
                        Help & Support
                      </a>
                      <div className="border-t border-gray-100">
                        <button
                          onClick={handleLogout}
                          className="flex items-center w-full px-4 py-2 text-sm text-red-700 hover:bg-red-50"
                        >
                          <span className="mr-3">🚪</span>
                          Sign Out
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  )
}

export default Header